"use client";
import useDeviceType from "@/hooks/useDeviceType";
import Image from "next/image";
import { useState, useEffect } from "react";

const LogoLoader = ({ 
  logoSrc = "/images/logo.png", 
  altText = "Company Logo",
  loadingTime = 3000 // Time in ms for loading to complete
}) => {
  const [loadingPercentage, setLoadingPercentage] = useState(0);
  const {isMobile} = useDeviceType();
  
  // Simulate loading progress
  useEffect(() => {
    const interval = setInterval(() => {
      setLoadingPercentage(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 1;
      });
    }, loadingTime / 100);
    
    return () => clearInterval(interval);
  }, [loadingTime]);
  
  return (
    <div className="container">
      {/* Logo with zoom animation */}
      <div className="logo-wrapper">
        <Image 
          src={logoSrc} 
          alt={"logo"}
          width={150}
          height={51}
          className="logo-image"
          sizes="150px"
          priority
        />
      </div>
      
      {/* Progress circle with percentage */}
      <div className="progress-container">
        <svg width="90" height="90" viewBox="0 0 90 90">
          {/* Background circle */}
          <circle
            cx="45"
            cy="45"
            r="40"
            fill="none"
            stroke="rgba(255,255,255,0.5)"
            strokeWidth="9"
          />
          {/* Progress circle */}
          <circle
            cx="45"
            cy="45"
            r="40"
            fill="none"
            stroke="rgba(255,255,255,0.9)"
            strokeWidth="9"
            strokeLinecap="round"
            strokeDasharray={251} // 2 * π * 40 ≈ 251
            strokeDashoffset={251 - (251 * loadingPercentage) / 100}
            transform="rotate(-90 45 45)"
          />
        </svg>
        {/* Percentage text in the middle of circle */}
        <div className="percentage-text">
          {`${Math.round(loadingPercentage)}%`}
        </div>
      </div>
      
      <style jsx>{`
        .container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: ${isMobile ? '20px' : '32px'};
        }
        
        .logo-wrapper {
          position: relative;
          width: 150px;
          height: 150px;
          display: flex;
          align-items: center;
          justify-content: center;
          animation: logoZoom 4s infinite ease-in-out;
        }
        
        .logo-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
        
        .progress-container {
          position: relative;
          width: ${isMobile ? '90px' : '120px'};
          height: ${isMobile ? '90px' : '120px'};
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .percentage-text {
          position: absolute;
          font-size: ${isMobile ? '1.25rem' : '1.5rem'};
          font-weight: 600;
          color: rgba(255,255,255,0.7);
        }
        
        @keyframes logoZoom {
          0% {
            transform: scale(1);
          }
          25% {
            transform: scale(1.15);
          }
          50% {
            transform: scale(0.95);
          }
          75% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default LogoLoader;